import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import SiteCTA from '@/components/SiteCTA';
import { 
  ArrowRight, 
  Clock, 
  Zap, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  Activity,
  Target,
  Sparkles,
  Timer,
  Rocket,
  ChevronRight,
  ChevronDown,
  Calendar,
  Users,
  BarChart3,
  Compass,
  HeartHandshake,
  Stethoscope,
  Wrench,
  LineChart,
  Ambulance,
  Map,
  Quote
} from 'lucide-react';

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 left-20 w-72 h-72 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 right-20 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 0],
          y: [0, 50, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

// 3D Card component with hover effects
const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const icons = {
    '🩺': Stethoscope,
    '🛠️': Wrench,
    '📊': LineChart,
    '🚑': Ambulance,
    '🧭': Compass,
    '🤝': HeartHandshake,
  };

  const IconComponent = icons[service.icon] || Zap;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.15 }}
      className="relative perspective-1000"
    >
      <div className="relative h-full">
        {/* Main card */}
        <div className="relative h-full bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl border border-gold/20 overflow-hidden backdrop-blur-xl">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]" />
          </div>
          
          {/* Content */}
          <div className="relative p-8 h-full flex flex-col">
            {/* Header with icon */}
            <div className="flex items-start justify-between mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-gold to-amber-400 rounded-2xl flex items-center justify-center shadow-2xl">
                <IconComponent className="w-8 h-8 text-black" />
              </div>
              {service.link && (
                <div className="text-gold">
                  <ArrowRight className="w-6 h-6" />
                </div>
              )}
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-white mb-4">
              {service.title}
            </h3>

            {/* Sections */}
            <div className="space-y-4 flex-grow">
              <div>
                <p className="text-gold font-semibold text-sm mb-2 uppercase tracking-wider">How We Help</p>
                <p className="text-gray-300 text-sm leading-relaxed">{service.help}</p>
              </div>

              <div>
                <p className="text-amber-400 font-semibold text-sm mb-2 uppercase tracking-wider">Perfect For</p>
                <p className="text-gray-300 text-sm leading-relaxed">{service.fit}</p>
              </div>
            </div>

            {/* Footer with timeline */}
            <div 
              className="mt-6 pt-6 border-t border-gold/20 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <Timer className="w-5 h-5 text-gold" />
                <span className="text-white font-medium">{service.time}</span>
              </div>
              {service.link && (
                <Link to={service.link}>
                  <Button 
                    size="sm" 
                    className="bg-gradient-to-r from-gold to-amber-400 text-black hover:from-amber-400 hover:to-gold font-semibold"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Animated corner accent */}
          <div
            className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold/20 to-transparent"
            style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}
          />
        </div>
      </div>
    </motion.div>
  );
};

// Value proposition component
const ValueProp = ({ icon: Icon, title, description, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay }}
      className="text-center"
    >
      <motion.div
        whileHover={{ rotate: 360 }}
        transition={{ duration: 0.5 }}
        className="w-20 h-20 bg-gradient-to-br from-gold to-amber-400 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl"
      >
        <Icon className="w-10 h-10 text-black" />
      </motion.div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      {description && <p className="text-gray-400 text-sm">{description}</p>}
    </motion.div>
  );
};

const QuickWinsPage = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  
  const quickWins = [
    {
      title: 'PMO Health & Maturity Assessments',
      icon: '🩺',
      help: 'Evaluation of current PMO Maturity, Project/Program Delivery, or Portfolio Health.',
      fit: "Quick diagnosis for leaders who suspect inefficiencies but don't know where to start.",
      time: '1–2 weeks',
    },
    {
      title: 'Process Optimization',
      icon: '🛠️',
      help: 'Tune-up engagements to improve project lifecycle, governance, processes, reporting and frameworks.',
      fit: 'Teams struggling with informed decision-making bottlenecks, initiative transparency, or delivery inconsistency.',
      time: '2–4 weeks',
    },
    {
      title: 'Project Portfolio Prioritization',
      icon: '📊',
      help: 'On-demand sessions to evaluate portfolio alignment, resourcing, and prioritization.',
      fit: 'Organizations struggling with project alignment to strategy, unclear priorities, and overwhelmed teams.',
      time: '2–3 weeks',
    },
    {
      title: 'Project Support, Triage, & Rescue',
      icon: '🚑',
      help: 'Tactical assessment and short-term intervention to triage and rescue troubled projects.',
      fit: 'When a high-impact project is off track and executives need rapid, experienced support to stabilize and recover momentum.',
      time: '1–3 weeks',
    },
    {
      title: 'Strategy & Alignment Workshops',
      icon: '🧭',
      help: 'Hands on workshops that turn strategy into how work gets done. Pick from OKRs, governance, intake, roadmap shaping, metrics, benefits realization, or request a custom on demand session.',
      fit: 'Leaders who need alignment now and a simple plan to move forward.',
      time: '1–2 days',
    },
    {
      title: 'Custom Services',
      icon: '🤝',
      help: 'Tailored engagements designed around your goals, challenges, and timelines.',
      fit: 'Leaders who want a bespoke scope aligned to outcomes.',
      time: 'Varies',
    },
  ];

  const valueProps = [
    { icon: Rocket, title: 'Rapid Deployment', description: '' },
    { icon: Target, title: 'Laser Focus', description: '' },
    { icon: TrendingUp, title: 'Immediate Impact', description: '' },
    { icon: Users, title: 'Expert Guidance', description: '' },
  ];

  return (
    <>
      <div className="min-h-screen bg-black text-white overflow-hidden">
        {/* Hero Section with Parallax */}
        <section className="relative min-h-screen flex items-center justify-center">
          <AnimatedBackground />
          
          <motion.div 
            style={{ y }}
            className="container mx-auto px-4 relative z-10 py-32"
          >
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-center max-w-5xl mx-auto"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
              >
                <Sparkles className="w-5 h-5 text-gold" />
                <span className="text-gold font-bold text-sm uppercase tracking-wider">Quick Wins & Targeted Solutions</span>
                <Sparkles className="w-5 h-5 text-gold" />
              </motion.div>
              
              {/* Main heading with gradient */}
              <motion.h1 
                className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold">
                  Rapid Impact
                </span>
                <br />
                <span className="text-white">
                  Engagements
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-12 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                On-demand PMO support that delivers immediate value and addresses specific challenges 
                impacting your business results and organizational performance.
              </motion.p>

              {/* Animated stats */}
              <motion.div 
                className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                {[
                  { number: '1-3', label: 'Weeks to Impact' },
                  { number: '6', label: 'Targeted Solutions' },
                  { number: '100%', label: 'Senior Consultants' },
                  { number: '3x', label: 'Average ROI' },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl md:text-4xl font-bold text-gold mb-2">{stat.number}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTA removed per request */}
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            className="absolute bottom-8 inset-x-0 flex justify-center"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col items-center gap-2 text-gold">
              <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
              <ChevronDown className="w-6 h-6" />
            </div>
          </motion.div>
        </section>

        {/* Value Props Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {valueProps.map((prop, index) => (
                <ValueProp key={prop.title} {...prop} delay={index * 0.1} />
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  OptiGold Quick Win Toolkit
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Six Powerful Services designed to deliver rapid results and immediate value
              </p>
              <div className="mt-10 flex justify-center">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-gradient-to-r from-gold to-amber-400 hover:from-amber-400 hover:to-gold text-black px-10 py-7 text-lg rounded-full shadow-2xl hover:shadow-gold/50 transition-all font-bold group"
                >
                  <a href="https://cal.com/noah.mccloud/optigold-pmo-quick-wins-targeted-solutions" className="flex items-center gap-3">
                    Schedule a Discovery Call
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </motion.div>

            {/* Services Grid */}
            <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {quickWins.map((service, index) => (
                <ServiceCard key={service.title} service={service} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Process Timeline */}
        <section className="py-20 relative bg-gradient-to-b from-black via-gray-900 to-black">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  The Quick Win Process
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Typical steps, we customize as needed
              </p>
            </motion.div>

            {/* Timeline */}
            <div className="max-w-4xl mx-auto">
              {[
                { day: 'Day 1', title: 'OptiGold Quick Win Toolkit', desc: 'A short call to confirm the problem, outcomes, stakeholders, and timeline.' },
                { day: '3-5 Days', title: 'Kickoff And Light Discovery', desc: 'We request essential docs and data, run a few targeted interviews, and align on success measures.' },
                { day: '7 - 10 Days', title: 'Rapid Working Sessions', desc: 'Focused engagement to diagnose, prioritize, and co design the fix for your specific Quick Win.' },
                { day: '7-10 Days', title: '*Action Sprint', desc: 'We implement the agreed changes or coach your team to do it, with tight check ins and visible progress.' },
                { day: '1 Day', title: 'Results And Handoff', desc: 'You get a summary, metrics baseline, and a 30 60 90 follow through plan. Ongoing support is available.' },
              ].map((step, index) => (
                <motion.div
                  key={step.day}
                  className="flex items-center gap-8 mb-8"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-gold to-amber-400 rounded-full flex items-center justify-center">
                      <span className="text-black font-bold text-2xl">{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="bg-gradient-to-r from-gray-900 to-transparent p-6 rounded-2xl border-l-4 border-gold">
                      <div className="text-gold font-bold mb-2">{step.day}</div>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <p className="text-gray-400">{step.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-400 text-sm mt-6 text-center">*Optional for Strategy & Alignment Workshops.</p>
          </div>
        </section>

        {/* Opportunities to Unlock Value */}
        <section className="py-20 relative bg-gradient-to-b from-black via-gray-900 to-black overflow-hidden">
          {/* Ambient orbs */}
          <motion.div
            className="absolute -top-10 -left-10 w-80 h-80 bg-gold/10 rounded-full blur-3xl"
            animate={{ x: [0, 30, 0], y: [0, 20, 0], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-10 -right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"
            animate={{ x: [0, -30, 0], y: [0, -20, 0], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold">
                  Opportunities to Unlock Value
                </span>
              </h2>
              <p className="text-gray-400 max-w-3xl mx-auto">
                Common signals we help address quickly and effectively
              </p>
            </motion.div>

            {(() => {
              const pains = [
                "We need a quick strategic review to align our portfolio with shifting business goals.",
                "We're struggling with transparency and need better executive-level reporting.",
                "We lack visibility into PMO maturity, making it hard to prioritize improvements.",
                "Our intake and governance process is too slow. We need help streamlining it.",
                "Our PM team is growing fast and needs better defined roles and responsibilities.",
                "One of our critical projects is off track, we need help stabilizing it and restoring stakeholder confidence.",
              ];
              return (
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pains.map((text, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.5, delay: idx * 0.05 }}
                      whileHover={{ y: -4, scale: 1.02 }}
                      className="relative group"
                    >
                      {/* Glow backdrop */}
                      <div className="absolute -inset-0.5 bg-gradient-to-br from-gold/0 via-gold/20 to-amber-400/0 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl border border-gold/20 rounded-2xl p-6 h-full">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold to-amber-400 flex items-center justify-center flex-shrink-0">
                            <Quote className="w-5 h-5 text-black" />
                          </div>
                          <p className="text-gray-200 leading-relaxed">{text}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              );
            })()}
          </div>
        </section>

        {/* CTA Section removed per request */}
      </div>

      <SiteCTA ctaHref="https://cal.com/noah.mccloud/optigold-pmo-quick-wins-targeted-solutions" />
    </>
  );
};

export default QuickWinsPage;
